import {
  type Props as AccountDropdownProps,
  AccountsDropdown,
} from "@/components/shared/select-inputs/accounts-dropdown";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useFormContext } from "react-hook-form";

type Props = {
  name: string;
  label?: string;
  options: AccountDropdownProps["items"];
  autoFocus?: boolean;
  placeholder?: AccountDropdownProps["placeholder"];
  description?: string;
  fullWidth?: AccountDropdownProps["fullWidth"];
  className?: AccountDropdownProps["className"];
  containerClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
  direction?: "horizontal" | "vertical";
  sideOffset?: number;
  allowReset?: boolean;
  allowUnknownAccount?: boolean;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
  variant?: "outline" | "ghost";
  container?: React.RefObject<HTMLElement>;
  modal?: boolean;
};

export const AccountDropdownInput = ({
  label,
  name,
  options,
  allowReset = false,
  fullWidth = true,
  placeholder,
  description,
  containerClassName,
  direction = "vertical",
  className,
  sideOffset,
  readOnly,
  disabled,
  variant = "outline",
  allowUnknownAccount,
  container,
  modal,
}: Props) => {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const currentOption = options.find(
          (option) => option.accountId === field.value,
        );

        const value = currentOption
          ? {
              label: currentOption.accountName,
              value: currentOption.accountId,
              subLabel: currentOption.accountNo,
              alternativeLabels: [],
            }
          : undefined;

        return (
          <FormItem>
            <div
              className={cn(
                direction === "horizontal" &&
                  "flex flex-row items-center space-x-4",
                containerClassName,
              )}
            >
              {label && <FormLabel>{label}</FormLabel>}
              <FormControl>
                <div className="w-full flex flex-row gap-1">
                  <AccountsDropdown
                    currentOption={value}
                    items={options}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                    className={className}
                    fullWidth={fullWidth}
                    placeholder={placeholder}
                    sideOffset={sideOffset}
                    variant={variant}
                    readOnly={readOnly}
                    disabled={disabled}
                    allowUnknownAccount={allowUnknownAccount}
                    container={container}
                    modal={modal}
                  />
                  {allowReset && value && (
                    <Button
                      type="reset"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        field.onChange(null);
                      }}
                      size="icon"
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              </FormControl>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </div>
          </FormItem>
        );
      }}
    />
  );
};
